import React, { useEffect, useState } from 'react';
import moment from 'moment/moment';
import Modal from 'react-modal';

import Select from 'react-select';
import { executeHttpRequest } from '../../epics/api';
import { UploadFile } from '../common/UploadFile';
import { ModalConfirmationWithErrorAndLoader } from '../common/ModalConfirmationWithErrorAndLoader';
import { ErrorMessage } from '../common';
import { getErrorMessages } from '../../utils';
import { TAdminComment, TAdminComments } from '../../types';
import { TCommentCategory } from './FreelancerDetails';

let axiosAbortController: AbortController;

export const MODAL_HEADER_TEXT_FOR_ADD_COMMENTS_OR_ATTACHMENT = 'Add Comment or Attachment';
export const MODAL_HEADER_TEXT_FOR_ADD_ATTACHMENT = 'Add Attachment';

type TCategoryOption = {
    label: string;
    value: number;
};

type TModalAddCommentWithAttachmentProps = {
    abortController: AbortController;
    commentDataForAddingAttachment?: TAdminComment;
    errorMessage: string;
    handleAddComment?: (
        setComment: (comment: string) => void,
        data: FormData,
        setUploadFileDetails: (file: File) => void,
        setUploadPercentage: (percent: number) => void,
        ajaxOptions: any,
    ) => void;
    handleAddAttachment?: (
        commentId: number,
        setComment: (comment: string) => void,
        data: FormData,
        setUploadFileDetails: (file: File) => void,
        setUploadPercentage: (percent: number) => void,
        ajaxOptions: any,
    ) => void;
    handleClose: () => void;
    isAttachmentMode?: boolean;
    isError: boolean;
    isOpen: boolean;
    categoryOptions: TCategoryOption[];
};

export function ModalAddCommentWithAttachment({
    abortController,
    commentDataForAddingAttachment,
    errorMessage,
    handleAddComment,
    handleAddAttachment,
    handleClose,
    isAttachmentMode,
    isError,
    isOpen,
    categoryOptions,
}: TModalAddCommentWithAttachmentProps) {
    const [category, setCategory] = useState<TCategoryOption>(null);
    const [comment, setComment] = useState(commentDataForAddingAttachment?.content ?? '');
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const [uploadFileDetails, setUploadFileDetails] = useState(null);

    const handleSave = () => {
        const data = new FormData();
        data.append('content', comment);
        if (category?.value) {
            data.append('category', category.value.toString());
        }
        if (uploadFileDetails) {
            data.append('file', uploadFileDetails);
        }

        const ajaxOptions = {
            onUploadProgress: (progressEvent: any) => {
                const { loaded, total } = progressEvent;
                const percent = Math.floor((loaded * 100) / total);

                if (percent < 100) {
                    setUploadPercentage(percent);
                }
            },
            signal: abortController.signal,
        };

        if (!isAttachmentMode) {
            handleAddComment(
                setComment,
                data,
                setUploadFileDetails,
                setUploadPercentage,
                ajaxOptions,
            );
        } else {
            handleAddAttachment(
                commentDataForAddingAttachment.id,
                setComment,
                data,
                setUploadFileDetails,
                setUploadPercentage,
                ajaxOptions,
            );
        }
    };

    const isFileBeingUploaded = !isAttachmentMode && uploadPercentage && !isError;

    return (
        <Modal
            className="add-file-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Add Comment or Attachment Modal"
        >
            <h4>
                {isAttachmentMode
                    ? MODAL_HEADER_TEXT_FOR_ADD_ATTACHMENT
                    : MODAL_HEADER_TEXT_FOR_ADD_COMMENTS_OR_ATTACHMENT}
            </h4>
            <div className="form">
                <Select
                    name="select-category"
                    className="admin-comment-category-select"
                    value={category}
                    onChange={event => setCategory(event)}
                    options={categoryOptions}
                    isDisabled={isFileBeingUploaded}
                    isClearable={true}
                    isSearchable={true}
                />
                <textarea
                    className="comment"
                    value={comment}
                    onChange={event => setComment(event.target.value)}
                    disabled={isFileBeingUploaded || isAttachmentMode}
                />
                <UploadFile
                    handleUploadFile={event =>
                        setUploadFileDetails(event.target.files[0])
                    }
                    uploadPercentage={uploadPercentage}
                    fileName={uploadFileDetails?.name}
                />
                {isError && <ErrorMessage message={errorMessage} />}
                <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        onClick={handleSave}
                        disabled={!!uploadPercentage && !isError}
                        type="button"
                    >
                        SAVE
                    </button>
                    <button
                        className="btn cancel-btn"
                        onClick={handleClose}
                        type="button"
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        </Modal>
    );
}

type TFreelancerDetailsAdminCommentsProps = {
    comments: TAdminComments;
    commentCategories: TCommentCategory[];
    editionEnabled: boolean;
    freelancerId: number;
    fetchListOf: (type: string, url: string) => Promise<void>;
}

export default function FreelancerDetailsAdminComments({
    comments,
    commentCategories,
    editionEnabled,
    freelancerId,
    fetchListOf,
}: TFreelancerDetailsAdminCommentsProps) {
    const [isModalAddCommentOpen, setIsModalAddCommentOpen] = useState(false);
    const [isModalGenerateYotiReportOpen, setIsModalGenerateYotiReportOpen] = useState(false);
    const [isModalResetYotiSessionOpen, setIsModalResetYotiSessionOpen] = useState(false);
    const [deletingID, setDeletingID] = useState(null);
    const [editingID, setEditingID] = useState(null);
    const [commentDataForAddingAttachment, setCommentDataForAddingAttachment] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [fields, setFields] = useState({
        category: null,
        content: null,
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        axiosAbortController = new AbortController();
    }, []);

    useEffect(() => {
        if (commentCategories) {
            const options = commentCategories
                .filter(category => category.active)
                .map(category => ({
                    label: category.name,
                    value: category.id,
                }));
            setCategoryOptions(options);
        }
    }, [commentCategories]);

    const handleOpenAddCommentModal = () => {
        axiosAbortController = new AbortController();
        setError(null);
        setIsModalAddCommentOpen(true);
    };

    const handleCloseAddCommentModal = () => {
        axiosAbortController.abort();
        setError(null);
        setIsModalAddCommentOpen(false);
    };

    const handleOpenAddAttachmentModal = (comment: TAdminComment) => {
        axiosAbortController = new AbortController();
        setError(null);
        setCommentDataForAddingAttachment(comment);
    };

    const handleCloseAddAttachmentModal = () => {
        axiosAbortController.abort();
        setError(null);
        setCommentDataForAddingAttachment(null);
    };

    const handleEdit = (comment: TAdminComment) => {
        setEditingID(comment.id);
        setFields({
            category: comment.category,
            content: comment.content,
        });
    };

    const clearState = () => {
        setError(null);
        setEditingID(null);
        setDeletingID(null);
        setFields({
            category: null,
            content: null,
        });
    };

    const handleOpenGenerateYotiReport = () => {
        setError(null);
        setIsModalGenerateYotiReportOpen(true);
    };

    const handleCloseGenerateYotiReport = () => {
        setError(null);
        setIsModalGenerateYotiReportOpen(false);
    };

    const handleOpenResetYotiReport = () => {
        setError(null);
        setIsModalResetYotiSessionOpen(true);
    };

    const handleCloseResetYotiReport = () => {
        setError(null);
        setIsModalResetYotiSessionOpen(false);
    };

    const handleAddAttachment = (
        commentId: number,
        setComment: (comment: string) => void,
        data: FormData,
        setUploadFileDetails: (file: File) => void,
        setUploadPercentage: (percent: number) => void,
        ajaxOptions: any,
    ) => {
        setError(null);

        executeHttpRequest(
            `freelancers/${freelancerId}/comments/${commentId}/`,
            'patch',
            data,
            true,
            ajaxOptions,
        )
            .then(() => {
                setComment('');
                setUploadPercentage(100);
                setUploadFileDetails(null);
                setCommentDataForAddingAttachment(null);

                fetchListOf(
                    'comments',
                    `freelancers/${freelancerId}/comments/`,
                ).then(() => {
                    handleCloseAddCommentModal();
                    setError(null);
                });
            })
            .catch(err => {
                setUploadPercentage(0);
                setError(getErrorMessages(err));
            });
    };

    const handleAddComment = (
        setComment: (comment: string) => void,
        data: FormData,
        setUploadFileDetails: (file: File) => void,
        setUploadPercentage: (percent: number) => void,
        ajaxOptions: any,
    ) => {
        setError(null);

        executeHttpRequest(
            `freelancers/${freelancerId}/comments/`,
            'post',
            data,
            true,
            ajaxOptions,
        )
            .then(() => {
                setComment('');
                setUploadPercentage(100);
                setUploadFileDetails(null);

                fetchListOf(
                    'comments',
                    `freelancers/${freelancerId}/comments/`,
                ).then(() => {
                    handleCloseAddCommentModal();
                    setError(null);
                });
            })
            .catch(err => {
                setUploadPercentage(0);
                setError(getErrorMessages(err));
            });
    };

    const handleUpdateComment = (comment: TAdminComment) => {
        setError(null);
        setEditingID(comment.id);

        executeHttpRequest(
            `freelancers/${freelancerId}/comments/${comment.id}/`,
            'patch',
            {
                ...fields,
            },
        )
            .then(() => {
                fetchListOf(
                    'comments',
                    `freelancers/${freelancerId}/comments/`,
                ).then(() => {
                    clearState();
                });
            })
            .catch(err => {
                setError(getErrorMessages(err));
            });
    };

    const handleDeleteComment = () => {
        setError(null);
        setIsLoading(true);

        executeHttpRequest(
            `freelancers/${freelancerId}/comments/${deletingID}/`,
            'delete',
        )
            .then(() => {
                setIsLoading(false);
                setDeletingID(null);

                fetchListOf(
                    'comments',
                    `freelancers/${freelancerId}/comments/`,
                ).then(() => {
                    clearState();
                });
            })
            .catch(err => {
                setError(getErrorMessages(err));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleGenerateYotiReport = () => {
        setError(null);
        setIsLoading(true);

        executeHttpRequest(
            `freelancers/${freelancerId}/yoti-session-report/`,
            'post',
        )
            .then(() => {
                fetchListOf(
                    'comments',
                    `freelancers/${freelancerId}/comments/`,
                ).then(() => {
                    setIsLoading(false);
                    handleCloseGenerateYotiReport();
                });
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const handleResetYotiReport = () => {
        setError(null);
        setIsLoading(true);

        executeHttpRequest(
            `freelancers/${freelancerId}/reset-yoti-session/`,
            'post',
        )
            .then(() => {
                fetchListOf(
                    'documents',
                    `documents/?freelancer=${freelancerId}`,
                ).then(() => {
                    setIsLoading(false);
                    handleCloseResetYotiReport();
                });
            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const renderActionButtons = (comment: TAdminComment) => {
        if (!editionEnabled) {
            return null;
        }

        if (editingID === comment.id) {
            return (
                <td className="actions center">
                    <button
                        onClick={() => handleUpdateComment(comment)}
                        type="button"
                    >
                        <i className="icon icon icon-check-12" />
                    </button>
                    <button onClick={() => clearState()} type="button">
                        <i className="icon icon-close2" />
                    </button>
                </td>
            );
        }

        return (
            <td className="actions center">
                <button onClick={() => handleEdit(comment)} type="button">
                    <i className="icon icon-pencil-3" />
                </button>
                <button
                    data-testid="delete"
                    disabled={deletingID === comment.id || !!comment.file}
                    onClick={() => setDeletingID(comment.id)}
                    type="button"
                >
                    <i className="icon icon-bin-1" />
                </button>
                <button
                    data-testid="add-attachment"
                    disabled={
                        commentDataForAddingAttachment?.id === comment.id ||
                        !!comment.file
                    }
                    onClick={() => handleOpenAddAttachmentModal(comment)}
                    type="button"
                >
                    <i className="icon icon-file-add-2" />
                </button>
            </td>
        );
    };

    const renderRows = () =>
        comments.content.map(comment => (
            <tr key={comment.id} data-testid={`table-row-${comment.id}`}>
                <td>
                    {moment(comment.created).format('D MMM YYYY, HH:mm')} -{' '}
                    {comment.author}
                </td>
                {editingID === comment.id ? (
                    <>
                        <td>
                            <Select
                                name="select-category"
                                className="admin-comment-category-select"
                                value={fields.category}
                                onChange={event =>
                                    setFields({
                                        ...fields,
                                        category: event ? event.value : '',
                                    })
                                }
                                options={categoryOptions}
                                isDisabled={isLoading}
                                isClearable={true}
                                isSearchable={true}
                            />
                        </td>
                        <td>
                            <textarea
                                className="edit-comment"
                                value={fields.content}
                                onChange={event =>
                                    setFields({
                                        ...fields,
                                        content: event.target.value,
                                    })
                                }
                            />
                        </td>
                    </>
                ) : (
                    <>
                        <td>{commentCategories.find(category => category.id === comment.category)?.name}</td>
                        <td>{comment.content}</td>
                    </>
                )}
                {comment.file ? (
                    <td>
                        <a
                            href={comment.file}
                            target="_blank"
                            rel="noreferrer"
                            download={true}
                        >
                            Open
                        </a>
                    </td>
                ) : (
                    <td>&nbsp;</td>
                )}
                {renderActionButtons(comment)}
            </tr>
        ));

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    Internal Comments and Attachments
                </div>
                <div className="freelancer-details-section__btns">
                    <button
                        className="freelancer-details-section__btn"
                        onClick={handleOpenAddCommentModal}
                        type="button"
                    >
                        <i className="icon with-text icon-add-12"/>
                        Add comment
                    </button>
                    <button
                        className="freelancer-details-section__gen-yoti-report-btn"
                        onClick={handleOpenGenerateYotiReport}
                        type="button"
                    >
                        <i className="icon with-text icon-file-pdf"/>
                        Generate<br/>Yoti report
                    </button>
                    <button
                        className="freelancer-details-section__gen-yoti-report-btn"
                        onClick={handleOpenResetYotiReport}
                        type="button"
                    >
                        <i className="icon with-text icon-synchronize-6"/>
                        Reset<br/>Yoti check
                    </button>
                    {!!comments.loading && (
                        <i className="icon icon-loading-1 font-size-30"/>
                    )}
                </div>
            </div>
            {error && !isModalAddCommentOpen && !isModalGenerateYotiReportOpen && !isModalResetYotiSessionOpen && (
                <div className="freelancer-details-section__errors">
                    <p>{error}</p>
                </div>
            )}
            <div className="freelancer-details-section__content">
                {comments.content ? (
                    <div>
                    <table className="table-default table-layout-fixed">
                            <tbody>
                            <tr>
                                <td className="table-default__cell--text-blue ">
                                    Date / Author
                                </td>
                                <td className="table-default__cell--text-blue med-col">
                                    Category
                                </td>
                                <td className="table-default__cell--text-blue">
                                    Comment
                                </td>
                                <td className="table-default__cell--text-blue sm-col">
                                    Attachment
                                </td>
                                {!!editionEnabled && (
                                    // eslint-disable-next-line max-len
                                    <td className="table-default__cell--text-blue center buttons-column"/>
                                )}
                            </tr>
                            {renderRows()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div>No internal comments have been left yet.</div>
                )}
            </div>
            {isModalAddCommentOpen && (
                <ModalAddCommentWithAttachment
                    abortController={axiosAbortController}
                    errorMessage={error}
                    handleAddComment={handleAddComment}
                    handleClose={handleCloseAddCommentModal}
                    isError={!!error}
                    isOpen={isModalAddCommentOpen}
                    categoryOptions={categoryOptions}
                />
            )}
            {commentDataForAddingAttachment && (
                <ModalAddCommentWithAttachment
                    abortController={axiosAbortController}
                    commentDataForAddingAttachment={commentDataForAddingAttachment}
                    errorMessage={error}
                    handleAddAttachment={handleAddAttachment}
                    handleClose={handleCloseAddAttachmentModal}
                    isAttachmentMode={true}
                    isError={!!error}
                    isOpen={!!commentDataForAddingAttachment}
                    categoryOptions={categoryOptions}
                />
            )}
            {deletingID !== null && (
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={clearState}
                    handleContinue={handleDeleteComment}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={deletingID !== null || !!error}
                    title="Deleting comment"
                >
                    Are you sure you want to delete this comment?
                </ModalConfirmationWithErrorAndLoader>
            )}
            {isModalGenerateYotiReportOpen && (
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={handleCloseGenerateYotiReport}
                    handleContinue={handleGenerateYotiReport}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={isModalGenerateYotiReportOpen}
                    title="Generating Yoti report"
                >
                    <p className="modal-title">Generate Yoti report</p>
                    <p>Are you sure you want to generate the Yoti verification report?</p><br/>
                </ModalConfirmationWithErrorAndLoader>
            )}
            {isModalResetYotiSessionOpen && (
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={error}
                    handleCancel={handleCloseResetYotiReport}
                    handleContinue={handleResetYotiReport}
                    isError={!!error}
                    isInProgress={isLoading}
                    setIsInProgress={setIsLoading}
                    isOpen={isModalResetYotiSessionOpen}
                    title="Reset Yoti check"
                >
                    <p className="modal-title">Reset Yoti check</p>
                    <p>Are you sure you wish to reset this worker’s YOTI check?</p><br/>
                </ModalConfirmationWithErrorAndLoader>
            )}
        </div>
    );
}
